import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { filterIndianCities } from "../../config/indianCities";
import "./LocationAutocomplete.css";

export default function LocationAutocomplete({
  value,
  onChange,
  onSelect,
  onCommit,
  onOpenChange,
  onFocusChange,
  placeholder = "City, state",
  className = "",
  readOnly = false,
  onReadOnlyActivate,
}) {
  const listId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [listStyle, setListStyle] = useState(null);
  const suggestions = filterIndianCities(query, 8);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const list = document.getElementById(listId);
        if (list && list.contains(e.target)) return;
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [listId]);

  useLayoutEffect(() => {
    if (!open || !suggestions.length || !wrapRef.current) {
      setListStyle(null);
      return undefined;
    }

    function updatePosition() {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setListStyle({
        position: "fixed",
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 1200,
      });
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, suggestions.length, query]);

  function pick(city) {
    setQuery(city.label);
    onSelect?.(city);
    setOpen(false);
  }

  const list =
    open && suggestions.length && listStyle ? (
      <ul
        id={listId}
        className="location-autocomplete__list location-autocomplete__list--portal"
        style={listStyle}
        role="listbox"
      >
        {suggestions.map((city) => (
          <li key={city.label}>
            <button
              type="button"
              role="option"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(city);
              }}
            >
              {city.area ? (
                <span className="location-autocomplete__option">
                  <span className="location-autocomplete__option-primary">{city.area}</span>
                  <span className="location-autocomplete__option-meta">
                    {[city.city, city.state].filter(Boolean).join(", ")}
                  </span>
                </span>
              ) : (
                <span className="location-autocomplete__option">
                  <span className="location-autocomplete__option-primary">{city.city}</span>
                  {city.state ? (
                    <span className="location-autocomplete__option-meta">{city.state}</span>
                  ) : null}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    ) : null;

  return (
    <div className={`location-autocomplete ${className}`.trim()} ref={wrapRef}>
      <span className="location-autocomplete__icon" aria-hidden="true">
        📍
      </span>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={query}
        placeholder={placeholder}
        readOnly={readOnly}
        onChange={(e) => {
          if (readOnly) return;
          setQuery(e.target.value);
          onChange?.(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          if (readOnly) {
            onReadOnlyActivate?.();
            return;
          }
          setOpen(true);
          onFocusChange?.(true);
        }}
        onClick={() => {
          if (readOnly) onReadOnlyActivate?.();
        }}
        onBlur={() => {
          if (readOnly) return;
          onFocusChange?.(false);
          onCommit?.(query);
        }}
      />
      {list ? createPortal(list, document.body) : null}
    </div>
  );
}
